import { TreeItems } from 'dnd-kit-sortable-tree';
import { useState } from 'react';
import {
  editTreeItem,
  removeTreeItems,
  tryInsertInSpecificFolder,
} from './documentsObserver';
import { BaseNode, cloneNodes } from './types/base';
import { showErrorToast } from 'components/uikit/toast/showToast';

export function useTreeActions<T extends BaseNode>({
  itemsRef,
  onChangeItemsWithInvalidation,
  onNodeDelete,
}: {
  itemsRef: React.RefObject<TreeItems<T>>;
  onChangeItemsWithInvalidation: (items: TreeItems<T>) => Promise<boolean>;
  onNodeDelete: (node: BaseNode) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const onItemRemove = async (id: string) => {
    const items = itemsRef.current;
    if (items.length > 0) {
      try {
        setIsLoading(true);
        await removeTreeItems(items, [id], onNodeDelete);
        await onChangeItemsWithInvalidation([...items]);
      } catch (e) {
        showErrorToast(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onBulkRemove = async (
    itemsToDelete: string[],
    onDelete?: () => Promise<void>,
  ) => {
    try {
      await onDelete?.();
    } catch (e) {
      showErrorToast(e);
      return;
    }

    const items = itemsRef.current;

    await removeTreeItems(items, itemsToDelete);
    await onChangeItemsWithInvalidation([...items]);
  };

  const onItemEdit = async (
    itemId: string,
    editedValue: string,
    editedProperty: 'name' | 'status',
  ) => {
    const items = itemsRef.current;
    editTreeItem(items, itemId, editedValue, editedProperty);
    await onChangeItemsWithInvalidation(items);
  };

  const onAddSubItem = async (folderId: string, node: BaseNode) => {
    const items_copy = cloneNodes(itemsRef.current);
    tryInsertInSpecificFolder(items_copy, folderId, node);
    const isInserted = await onChangeItemsWithInvalidation(items_copy);

    if (isInserted) {
      itemsRef.current = items_copy;
    }
  };

  return {
    isLoading,
    setIsLoading,
    onItemRemove,
    onItemEdit,
    onAddSubItem,
    onBulkRemove,
  };
}
